import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Invoice, NormalizedMovement, InvoiceWalletMovementsResponse } from '../types';
import { apiService } from '../services/api';

interface Props {
  invoice: Invoice;
  onRefreshInvoice?: () => void;
}

const InvoiceDetailsIbanFirstTab: React.FC<Props> = ({ invoice, onRefreshInvoice }) => {
  const [walletId, setWalletId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<InvoiceWalletMovementsResponse | null>(null);
  const [search, setSearch] = useState<string>('');

  const windowDays = { before: 30, after: 30 };

  // Date helpers and UI state for filtering (cap upper bound at today)
  const formatYmd = (d: Date) => d.toISOString().slice(0, 10);
  const todayStr = formatYmd(new Date());

  const [fromDate, setFromDate] = useState<string>(() => {
    const due = new Date(invoice.due_date || invoice.invoice_date);
    const start = new Date(due);
    start.setDate(start.getDate() - windowDays.before);
    return formatYmd(start);
  });
  const [toDate, setToDate] = useState<string>(() => todayStr);

  const clampToToday = (s: string) => (s > todayStr ? todayStr : s);
  const getMovementISODate = (mv: NormalizedMovement): string | null => {
    const raw = mv.bookingDate || mv.date;
    if (!raw) return null;
    try {
      return formatYmd(new Date(raw));
    } catch {
      return null;
    }
  };

  
  const fetchMovements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use selected date range; cap upper bound at today
      const toClamped = clampToToday(toDate);

      // Single request for entire range; backend will auto-sync if needed
      const res = await apiService.invoices.getInvoiceWalletMovementsRange(invoice.invoice_id, {
        walletId: walletId || undefined,
        fromDate,
        toDate: toClamped,
      });

      // De-duplicate by id/date+amount just in case
      const seen = new Set<string>();
      const unique = (res.movements || []).filter(mv => {
        const k = mv.id || `${mv.bookingDate || mv.date}-${mv.amount}-${mv.currency}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });

      setData({
        success: true,
        invoice_id: invoice.invoice_id,
        window: res.window || { from: fromDate, to: toClamped },
        count: unique.length,
        movements: unique,
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load wallet movements');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id, walletId, fromDate, toDate]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  const positiveMovements = useMemo<NormalizedMovement[]>(() => {
    const list = data?.movements || [];
    const toClamped = clampToToday(toDate);
    return list
      .filter(m => (m.amount || 0) > 0)
      .filter(mv => {
        const aligned = Boolean((mv as any).is_aligned);
        const alignedToCurrent = Boolean((mv as any).aligned_to_current_invoice);
        // Hide movements already aligned to other invoices
        return !aligned || alignedToCurrent;
      })
      .filter(mv => {
        const d = getMovementISODate(mv);
        if (!d) return true;
        return d >= fromDate && d <= toClamped;
      });
  }, [data, fromDate, toDate]);

  // Filter movements by simple search across multiple fields
  const displayedMovements = useMemo<NormalizedMovement[]>(() => {
    const list = positiveMovements;
    if (!search) return list;
    const needle = search.toLowerCase();
    return list.filter(mv => {
      const hay = [
        mv.remittanceInformation || mv.reference || '',
        mv.orderingCustomer || '',
        mv.description || '',
        ...(Array.isArray((mv as any).invoice_tokens) ? (mv as any).invoice_tokens : []),
        mv.id || '',
      ].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [positiveMovements, search]);

  // Derived matching checks: compare ordering customer to invoice customer and amounts to invoice total
  const movementChecks = useMemo(() => {
    const invCustomer = (invoice.customer_name || '').toLowerCase();
    const invAmount = Number(invoice.total_amount || 0);
    const invCurrency = invoice.currency;
    return new Map(
      (positiveMovements || []).map(mv => {
        const payer = (mv.orderingCustomer || mv.description || mv.reference || '').toLowerCase();
        const nameMatch = invCustomer && payer ? payer.includes(invCustomer) || invCustomer.includes(payer) : false;
        const creditedVal = typeof mv.amount === 'number' ? mv.amount : (mv.beneficiaryAmount?.value ?? NaN);
        const creditedCur = mv.currency || mv.beneficiaryAmount?.currency || invCurrency;
        const epsilon = 0.01; // cents tolerance
        const amountMatch = creditedCur === invCurrency && Math.abs(creditedVal - invAmount) <= epsilon;
        return [mv, { nameMatch, amountMatch } as { nameMatch: boolean; amountMatch: boolean }];
      })
    );
  }, [positiveMovements, invoice.customer_name, invoice.total_amount, invoice.currency]);

  const alignMovement = useCallback(async (movement: NormalizedMovement) => {
    if (!movement.id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.invoices.alignBankTransfer(invoice.invoice_id, movement.id);
      // If backend demands confirmation
      if ((res as any)?.requires_confirmation) {
        const proceed = window.confirm('Movement does not strongly match by token or amount. Force alignment?');
        if (!proceed) return;
        const forced = await apiService.invoices.alignBankTransfer(invoice.invoice_id, movement.id, true);
        if ((forced as any)?.success) {
          alert('Payment aligned successfully.');
          // Refresh invoice state in parent and reload movements
          onRefreshInvoice?.();
          fetchMovements();
        } else {
          throw new Error((forced as any)?.message || 'Failed to align payment');
        }
      } else if ((res as any)?.success) {
        alert('Payment aligned successfully.');
        onRefreshInvoice?.();
        fetchMovements();
      } else {
        throw new Error((res as any)?.message || 'Failed to align payment');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to align payment');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id, onRefreshInvoice, fetchMovements]);

  const disalignMovement = useCallback(async (movement: NormalizedMovement) => {
    if (!movement.id) return;
    try {
      setLoading(true);
      setError(null);
      // Backend endpoint to disalign and set invoice unpaid
      const res = await apiService.invoices.disalignBankTransfer(invoice.invoice_id, movement.id);
      if ((res as any)?.success) {
        alert('Payment disaligned. Invoice set to unpaid.');
        onRefreshInvoice?.();
        fetchMovements();
      } else {
        throw new Error((res as any)?.message || 'Failed to disalign payment');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to disalign payment');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id, onRefreshInvoice, fetchMovements]);

  return (
    <div className="ibanfirst-tab">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h3 style={{ margin: 0 }}>Bank Transfers</h3>
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <label className="form-label mb-0" style={{ fontWeight: 500 }}>Wallet ID (optional)</label>
            <input
              className="form-control"
              style={{ width: 220 }}
              placeholder="e.g. WAL-XXXX"
              value={walletId}
              onChange={(e) => setWalletId(e.target.value)}
            />
            <label className="form-label mb-0" style={{ fontWeight: 500 }}>From</label>
            <input
              type="date"
              className="form-control"
              style={{ width: 160 }}
              value={fromDate}
              max={clampToToday(toDate)}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                setFromDate(v);
                if (v > toDate) setToDate(v);
              }}
            />
            <label className="form-label mb-0" style={{ fontWeight: 500 }}>To</label>
            <input
              type="date"
              className="form-control"
              style={{ width: 160 }}
              value={toDate}
              min={fromDate}
              max={todayStr}
              onChange={(e) => {
                let v = e.target.value;
                if (!v) return;
                if (v > todayStr) v = todayStr;
                setToDate(v);
                if (v < fromDate) setFromDate(v);
              }}
            />
            <input
              className="form-control"
              style={{ width: 240 }}
              placeholder="Search movements (reference, payer, desc, token)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn btn-outline-secondary" onClick={fetchMovements} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <div className="card-body">
          <p className="text-muted" style={{ marginTop: 0 }}>
            Showing positive wallet movements from {fromDate} to {clampToToday(toDate)}. Future dates are excluded automatically. Movements already aligned to other invoices are hidden. Use Align to mark this invoice as paid by bank transfer.
          </p>

          {error && (
            <div className="alert alert-danger" role="alert">{error}</div>
          )}

          {loading && !data && (
            <div className="loading-container"><div className="spinner" /></div>
          )}

          {!loading && positiveMovements.length === 0 && (
            <div className="empty-state">
              <h4 style={{ color: '#7f8c8d', marginBottom: 10 }}>No positive movements found</h4>
              <p className="text-muted" style={{ margin: 0 }}>Try adjusting the wallet or time window.</p>
            </div>
          )}

          {displayedMovements.length > 0 && (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th style={{ minWidth: 120 }}>Booking Date</th>
                    <th style={{ minWidth: 120 }}>Value Date</th>
                    <th style={{ minWidth: 120, textAlign: 'right' }}>Amount</th>
                    <th style={{ minWidth: 90 }}>Currency</th>
                    <th>Remittance Info</th>
                    <th>Ordering Customer</th>
                    <th>Description</th>
                    <th style={{ minWidth: 180 }}>Match</th>
                    <th style={{ minWidth: 150 }}>Aligned</th>
                    <th style={{ minWidth: 220 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedMovements.map((mv) => {
                    const booking = mv.bookingDate ? new Date(mv.bookingDate).toLocaleDateString() : '—';
                    const valued = mv.date ? new Date(mv.date).toLocaleDateString() : '—';
                    const remit = mv.remittanceInformation || mv.reference || '—';
                    const payer = mv.orderingCustomer || '—';
                    const amount = (mv.beneficiaryAmount?.value ?? mv.amount ?? 0).toFixed(2);
                    const currency = mv.beneficiaryAmount?.currency || mv.currency || invoice.currency;
                    const matchedAt = (mv as any).matched_at ? new Date((mv as any).matched_at).toLocaleString() : '—';
                    const isAligned = Boolean((mv as any).aligned_to_current_invoice);
                    return (
                      <tr key={mv.id || `${mv.bookingDate || mv.date}-${mv.amount}`}>
                        <td>{booking}</td>
                        <td>{valued}</td>
                        <td style={{ textAlign: 'right' }}>{amount}</td>
                        <td>{currency}</td>
                        <td>{remit}</td>
                        <td>{payer}</td>
                        <td>{mv.description || '—'}</td>
                        <td>
                          <div className="d-flex align-items-center" style={{ gap: 4, flexWrap: 'wrap' }}>
                            {(mv.invoice_tokens && mv.invoice_tokens.length > 0) ? (
                              mv.invoice_tokens.map(tok => (
                                <span key={tok} className="badge bg-secondary">{tok}</span>
                              ))
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                            {/* Matching hints */}
                            {(() => {
                              const checks = movementChecks.get(mv) || { nameMatch: false, amountMatch: false };
                              const tokens = mv.invoice_tokens || [];
                              const tokenMatch = Boolean(invoice.invoice_name && tokens.includes(invoice.invoice_name));
                              const invRefStr = (invoice.invoice_name || String(invoice.invoice_number || '')).toString();
                              const refLower = invRefStr.toLowerCase();
                              const remitLower = (remit || '').toLowerCase();
                              const descLower = (mv.description || '').toLowerCase();
                              const refMatch = !!(refLower && (remitLower.includes(refLower) || descLower.includes(refLower)));
                              return (
                                <>
                                  {tokenMatch && <span className="badge bg-info">Token</span>}
                                  {checks.amountMatch && <span className="badge bg-success">Amount</span>}
                                  {checks.nameMatch && <span className="badge bg-primary">Customer</span>}
                                  {refMatch && <span className="badge bg-warning text-dark">Invoice #</span>}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td>
                          {isAligned ? (
                            <>
                              <span className="badge bg-success">Aligned</span>
                              <div className="text-muted" style={{ fontSize: 12 }}>at {matchedAt}</div>
                            </>
                          ) : (
                            <span className="badge bg-secondary">Not Aligned</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            {isAligned ? (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => disalignMovement(mv)}
                                disabled={loading || !mv.id}
                                title={!mv.id ? 'Missing movement ID' : ''}
                              >
                                Disalign
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => alignMovement(mv)}
                                disabled={loading || !mv.id}
                                title={!mv.id ? 'Missing movement ID' : ''}
                              >
                                Align
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => fetchMovements()}
                            >
                              Refresh
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && positiveMovements.length > 0 && displayedMovements.length === 0 && (
            <div className="empty-state">
              <h4 style={{ color: '#7f8c8d', marginBottom: 10 }}>No results match your search</h4>
              <p className="text-muted" style={{ margin: 0 }}>Clear the search or adjust the time window.</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .ibanfirst-tab .empty-state {
          text-align: center;
          padding: 40px 20px;
        }
        .ibanfirst-tab .loading-container {
          text-align: center;
          padding: 40px 20px;
        }
      `}</style>
    </div>
  );
};

export default InvoiceDetailsIbanFirstTab;
